import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { cancelAccount, getSubscription } from '../../data/api';
import SpinnerButton from './SpinnerButton';

/**
 * @typedef {object} CancelImmediatelyProps
 * @property {boolean} isLoading
 * @property {object} billingData
 * @property {string} subscriptionId
 * @property {function} onCloseModal
 * @property {function} setLoadingState
 * @property {function} setError
 * @property {function} setIsLoading
 */

/**
 * @param {CancelImmediatelyProps} props
 * @returns {JSX.Element}
 */
function CancelImmediately({
    isLoading,
    billingData,
    subscriptionId,
    onCloseModal,
    setLoadingState,
    setError,
    setIsLoading,
}) {
    const buttonStates = {
        success: {
            className: 'gh-btn gh-btn-block gh-btn-red',
            html: '&#10004; Successfully cancelled!',
            disabled: true,
        },
        fail: {
            className: 'gh-btn gh-btn-block gh-btn-red',
            html: 'Contact support@ghost.org',
            disabled: true,
        },
        pending: {
            className: 'gh-btn gh-btn-red gh-btn-block spinner',
            html: '<span><div class="gh-spinner"></div></span>',
            disabled: true,
        },
        default: {
            className: 'gh-btn gh-btn-block gh-btn-red',
            html: 'Cancel subscription and delete all data',
            onSubmit: 'cancel',
            disabled: true,
        },
    };
    const [buttonState, setButtonState] = useState(buttonStates.default);
    // Timer handles live in refs (not state) so their current ids are always
    // available to clear them — from the running callbacks and from the unmount
    // cleanup below. Keeping the interval id in state caused a stale closure
    // where clearInterval received the old `null` and the poll never stopped.
    const pollingIntervalRef = useRef(null);
    const cancelTimeoutRef = useRef(null);
    const [cancelToggle, setCancelToggle] = useState({
        download: false,
        deleted: false,
        recover: false,
    });

    // Clear the 90s cancel-timeout and the polling interval when the modal
    // unmounts, so real timers can't leak (they otherwise keep firing — hanging
    // tests, and polling + setState on an unmounted component if the user closes
    // the modal mid-cancel).
    useEffect(() => {
        return () => {
            if (cancelTimeoutRef.current) {
                clearTimeout(cancelTimeoutRef.current);
            }
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    const closeModalAndRedirect = () => {
        setLoadingState(true);

        // Reload the data, so going back in history wouldn't provide wrong information
        onCloseModal({ reload: true, delay: 1000 });

        // Redirect the user to ghost.org. This is like a 'fake' signing out process
        // because going back in browser history afterwards will already show the
        // offline.ghost.org page
        return (window.parent.location.href = 'https://ghost.org');
    };

    /**
     * Takes the subscriptionId and checks if the subscription has been cancelled in Daisy
     * @param {{subscriptionId: string}} options
     * @returns {Promise<void>}
     */
    const checkSubscriptionCancelled = async ({ subscriptionId: subId }) => {
        try {
            // Actually proceed with the cancellation which will also suspend the site
            const res = await getSubscription({
                subId,
                includeCancelled: true,
            });

            if (res?.subscription) {
                if (res?.subscription?.attributes?.status === 'canceled') {
                    // Cancellation succeeded — stop polling AND disarm the 90s
                    // fallback timeout so it can't later flip the modal into the
                    // timeout failure state after a successful cancel.
                    clearInterval(pollingIntervalRef.current);
                    clearTimeout(cancelTimeoutRef.current);
                    return closeModalAndRedirect();
                }
            }
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    source: 'cancelModal',
                    shown_to_user: true,
                },
                extra: {
                    subscriptionId: subId,
                },
                user: {
                    id: billingData?.user?.id,
                    email: billingData?.user?.email_address,
                    username: billingData?.user?.stripe_token_sg,
                },
            });

            clearInterval(pollingIntervalRef.current);
        }
    };

    /**
     * Handles the immediate cancellation of the account by cancelling the subscription
     * with the background API method and polling the subscription endpoint to check if
     * the subscription has been cancelled.
     * @param {React.MouseEvent<HTMLButtonElement>} event
     * @returns {Promise<void>}
     */
    const handleCancelImmediately = async (event) => {
        event.preventDefault();
        // Set loading state. Can only be done at this point, because the
        // Stripe card elements needs to be mounted until now
        setIsLoading(true);
        setButtonState(buttonStates.pending);

        try {
            // Actually proceed with the cancellation which will also suspend the site
            const res = await cancelAccount({ subscriptionId });

            if (res?.ok) {
                // create a timeout to cancel the cancellation request after
                // 90 seconds and show and error to the user instead
                cancelTimeoutRef.current = setTimeout(() => {
                    const loggingError = new Error(
                        `Cancelling timed out: ${res?.error?.error?.message || res?.error?.message}`,
                    );
                    Sentry.captureException(loggingError, {
                        tags: {
                            source: 'cancelModal',
                            shown_to_user: true,
                        },
                        contexts: {
                            full_error: res?.error,
                        },
                        extra: {
                            subscriptionId,
                        },
                        user: {
                            id: billingData?.user?.id,
                            email: billingData?.user?.email_address,
                            username: billingData?.user?.stripe_token_sg,
                        },
                    });

                    setIsLoading(false);
                    setButtonState(buttonStates.fail);
                    setError(
                        'Something went wrong when trying to cancel. Refresh your browser and try again, or contact support@ghost.org',
                    );
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                    }
                }, 90 * 1000);

                // Start polling the subscription endpoint to check
                // if the subscription has been cancelled
                pollingIntervalRef.current = setInterval(async () => {
                    await checkSubscriptionCancelled({ subscriptionId });
                }, 4 * 1000);
                return;
            } else {
                const loggingError = new Error(
                    `Cancelling failed: ${res?.error?.error?.message || res?.error?.message}`,
                );
                Sentry.captureException(loggingError, {
                    tags: {
                        source: 'cancelModal',
                        shown_to_user: true,
                    },
                    contexts: {
                        full_error: res?.error,
                    },
                    extra: {
                        subscriptionId,
                    },
                    user: {
                        id: billingData?.user?.id,
                        email: billingData?.user?.email_address,
                        username: billingData?.user?.stripe_token_sg,
                    },
                });

                setIsLoading(false);
                setButtonState(buttonStates.fail);
                return setError(
                    'Something went wrong when trying to cancel. Refresh your browser and try again, or contact support@ghost.org',
                );
            }
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    source: 'cancelModal',
                    shown_to_user: true,
                },
                user: {
                    id: billingData?.user?.id,
                    email: billingData?.user?.email_address,
                    username: billingData?.user?.stripe_token_sg,
                },
                extra: {
                    subscriptionId,
                },
            });

            setIsLoading(false);
            setButtonState(buttonStates.fail);
            return setError(
                'Something went wrong when trying to cancel. Refresh your browser and try again, or contact support@ghost.org',
            );
        }
    };

    /**
     * Handles the toggling of the checkboxes and only enables the cancel button
     * when all checkboxes are checked
     * @param {React.ChangeEvent<HTMLInputElement>} event
     * @param {string} checkbox
     */
    const handleCancelToggle = (event, checkbox) => {
        cancelToggle[checkbox] = event.target.checked;
        setCancelToggle(cancelToggle);

        const checkBoxValues = Object.values(cancelToggle);

        // Only enable the cancel confirm button when all toggles are checked
        if (checkBoxValues.length >= 3 && checkBoxValues.every((val) => val === true)) {
            setButtonState({ ...buttonStates.default, disabled: false });
        } else {
            setButtonState(buttonStates.default);
        }
    };

    /**
     * @param {React.MouseEvent<HTMLButtonElement>} event
     * @returns {Promise<void>}
     */
    const setSpinnerButtonAction = (event) => {
        if (buttonState?.disabled || !buttonState?.onSubmit) {
            return null;
        } else if (buttonState?.onSubmit === 'cancel') {
            return handleCancelImmediately(event);
        }
    };

    return (
        <>
            <div className="cancel-account-warning">
                <p>Here's what happens when you cancel:</p>
                <ul>
                    <li>Your subscription will stop and you won’t be billed again.</li>
                    <li>Your Ghost(Pro) account will be closed immediately.</li>
                    <li>
                        Your account and publication data will be permanently deleted. Remember to
                        export your data before you cancel.
                    </li>
                </ul>
            </div>

            <div className="gh-input-group">
                <span className="toggle-inline">
                    <input
                        id="checkbox-download"
                        className="toggle-btn"
                        type="checkbox"
                        name="download"
                        disabled={isLoading}
                        onChange={(e) => handleCancelToggle(e, 'download')}
                    />
                    <label htmlFor="download">I have downloaded my data / don’t need my data</label>
                </span>
                <span className="toggle-inline">
                    <input
                        id="checkbox-deleted"
                        className="toggle-btn"
                        type="checkbox"
                        name="deleted"
                        disabled={isLoading}
                        onChange={(e) => handleCancelToggle(e, 'deleted')}
                    />
                    <label htmlFor="deleted">
                        I understand that my data will be permanently deleted
                    </label>
                </span>
                <span className="toggle-inline">
                    <input
                        id="checkbox-recover"
                        className="toggle-btn"
                        type="checkbox"
                        name="recover"
                        disabled={isLoading}
                        onChange={(e) => handleCancelToggle(e, 'recover')}
                    />
                    <label htmlFor="recover">I understand that my data cannot be recovered</label>
                </span>
            </div>

            <SpinnerButton
                onClick={(event) => setSpinnerButtonAction(event)}
                className={buttonState.className}
                html={buttonState.html}
                disabled={buttonState.disabled}
                data-test-btn="cancel-account-now"
            />
        </>
    );
}

CancelImmediately.propTypes = {
    isLoading: PropTypes.bool.isRequired,
    billingData: PropTypes.object.isRequired,
    subscriptionId: PropTypes.string.isRequired,
    onCloseModal: PropTypes.func.isRequired,
    setLoadingState: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
    setIsLoading: PropTypes.func.isRequired,
};

export default CancelImmediately;
